import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useMyShiftSwapApi } from './config/useMyShiftSwapApi';
import { peerAcceptSwap, peerRejectSwap, cancelSwap } from '../../services/shiftSwapService';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

// Rol × durum uygunluk matrisi (brief'in eşleşmesi, verbatim). Hem aksiyonlar
// sütununun görünürlük kapısında hem her menü öğesinin `condition`'ında paylaşılır
// (tek doğruluk kaynağı) — modül seviyesinde: bileşen her render'da yeniden
// oluşturmaz, deps dizilerine girmesi gerekmez.
const canAcceptOrReject = (row) => row?.role === 'target' && row?.status === 'pending_peer';
const canCancelSwap = (row) => row?.role === 'requester' && (row?.status === 'pending_peer' || row?.status === 'pending_manager');

export default function MyShiftSwapsPage() {
  const { t } = useTranslation();

  const { columns: baseColumns, data, fetching, refresh } = useMyShiftSwapApi();

  const [acceptConfirm, setAcceptConfirm] = useState({ isOpen: false, id: null });
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null });
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleAcceptClick = useCallback((row) => {
    setAcceptConfirm({ isOpen: true, id: row.id });
  }, []);

  // No try/catch here on purpose — same reasoning as SwapRequestModal.js's
  // handleSave: the Axios interceptor already toasts mutation errors and rejects
  // the promise, so a rejection here simply means the .then() below never runs
  // and the dialog stays open. `finally` only clears local processing/dialog state.
  const handleAcceptConfirm = useCallback(() => {
    setProcessing(true);
    peerAcceptSwap(acceptConfirm.id, { note: '' })
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setAcceptConfirm({ isOpen: false, id: null });
      });
  }, [acceptConfirm.id, refresh]);

  const handleRejectOpen = useCallback((row) => {
    setRejectReason('');
    setRejectDialog({ isOpen: true, id: row.id });
  }, []);

  const handleRejectConfirm = useCallback(() => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    peerRejectSwap(rejectDialog.id, { note: rejectReason })
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setRejectDialog({ isOpen: false, id: null });
      });
  }, [rejectDialog.id, rejectReason, refresh]);

  const handleCancelClick = useCallback((row) => {
    setCancelConfirm({ isOpen: true, id: row.id });
  }, []);

  const handleCancelConfirm = useCallback(() => {
    setProcessing(true);
    cancelSwap(cancelConfirm.id)
      .then(() => refresh())
      .finally(() => {
        setProcessing(false);
        setCancelConfirm({ isOpen: false, id: null });
      });
  }, [cancelConfirm.id, refresh]);

  const actionOptions = useMemo(() => [
    { type: 'accept', label: t('shiftSwap.action.accept'), onClick: handleAcceptClick, condition: canAcceptOrReject },
    { type: 'reject', label: t('shiftSwap.action.reject'), onClick: handleRejectOpen, condition: canAcceptOrReject },
    { type: 'cancel', label: t('shiftSwap.action.cancel'), onClick: handleCancelClick, condition: canCancelSwap },
  ], [t, handleAcceptClick, handleRejectOpen, handleCancelClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          // No eligible action at all (approved/canceled/rejected_* rows, or a
          // pending row where the viewer is neither the deciding peer nor the
          // original requester) — render nothing rather than an empty "..." menu
          // (mirrors AssetAssignmentsPage.js's `if (row?.returnDate) return null;`).
          if (!row || (!canAcceptOrReject(row) && !canCancelSwap(row))) return null;
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={acceptConfirm.isOpen}
        title={t('shiftSwap.acceptTitle')}
        message={t('shiftSwap.acceptMessage')}
        onConfirm={handleAcceptConfirm}
        onCancel={() => setAcceptConfirm({ isOpen: false, id: null })}
        confirmText={t('shiftSwap.action.accept')}
        loading={processing}
      />

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('shiftSwap.cancelTitle')}
        message={t('shiftSwap.cancelMessage')}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null })}
        confirmText={t('shiftSwap.action.cancel')}
        loading={processing}
      />

      {rejectDialog.isOpen && (
        <RejectOverlay onClick={() => setRejectDialog({ isOpen: false, id: null })}>
          <RejectDialog onClick={(e) => e.stopPropagation()}>
            <RejectTitle>{t('shiftSwap.rejectTitle')}</RejectTitle>
            <TextInput
              label={t('shiftSwap.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialog({ isOpen: false, id: null })} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}
                style={{ background: '#d32f2f' }}>
                {processing ? '...' : t('shiftSwap.action.reject')}
              </Button>
            </div>
          </RejectDialog>
        </RejectOverlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('shiftSwap.myList.title')}
          serverSide={false}
          fetching={fetching}
          columns={columns}
          data={data}
          pageSize={10}
        />
      ), [t, fetching, columns, data])}
    </Container>
  );
}
